/**
 * 最適化用の共通ユーティリティ
 * 重複するロジックを統合し、パフォーマンスを向上させます
 */

import debounce from 'lodash-es/debounce';

/**
 * ローカルストレージの操作を最適化したラッパー
 */
export class OptimizedLocalStorage {
  private static instance: OptimizedLocalStorage;
  private cache = new Map<string, any>();
  private debouncedSetters = new Map<
    string,
    { fn: ReturnType<typeof debounce>; debounceMs: number; pendingValue: unknown }
  >();

  static getInstance(): OptimizedLocalStorage {
    if (!OptimizedLocalStorage.instance) {
      OptimizedLocalStorage.instance = new OptimizedLocalStorage();
      // ページアンロード時に保留中の書き込みをフラッシュ
      OptimizedLocalStorage.instance.setupUnloadHandler();
    }
    return OptimizedLocalStorage.instance;
  }

  /**
   * ページアンロード時に保留中の書き込みをフラッシュ
   */
  private setupUnloadHandler(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const flushAll = () => {
      this.flush();
    };

    // beforeunloadとvisibilitychangeの両方でフラッシュ
    window.addEventListener('beforeunload', flushAll);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushAll();
      }
    });
  }

  /**
   * 値を取得（キャッシュ優先）
   */
  get<T>(key: string, defaultValue: T): T {
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return defaultValue;
      }
      const value = JSON.parse(stored);
      this.cache.set(key, value);
      return value;
    } catch {
      return defaultValue;
    }
  }

  /**
   * 値を設定（デバウンス付き）
   */
  set(key: string, value: unknown, debounceMs: number = 100): void {
    // キャッシュを即座に更新
    this.cache.set(key, value);

    // デバウンス関数を作成または取得
    let debouncedSet = this.debouncedSetters.get(key);
    if (!debouncedSet || debouncedSet.debounceMs !== debounceMs) {
      // 既存のデバウンス関数をキャンセル
      if (debouncedSet) {
        debouncedSet.fn.cancel();
      }

      // 新しいデバウンス関数を作成
      const fn = debounce(() => {
        const entry = this.debouncedSetters.get(key);
        if (entry) {
          try {
            localStorage.setItem(key, JSON.stringify(entry.pendingValue));
          } catch {
            // エラー時は無視
          }
        }
      }, debounceMs);

      debouncedSet = { fn, debounceMs, pendingValue: value };
      this.debouncedSetters.set(key, debouncedSet);
    } else {
      // 既存のデバウンス関数がある場合は値のみ更新
      debouncedSet.pendingValue = value;
    }

    // デバウンスされた保存を実行
    debouncedSet.fn();
  }

  /**
   * 値を削除
   */
  remove(key: string): void {
    this.cache.delete(key);
    const debouncedSet = this.debouncedSetters.get(key);
    if (debouncedSet) {
      debouncedSet.fn.cancel();
      this.debouncedSetters.delete(key);
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // エラー時は無視
    }
  }

  /**
   * 保留中の書き込みを即座にフラッシュ
   */
  flush(): void {
    for (const [key, { fn, pendingValue }] of this.debouncedSetters.entries()) {
      // デバウンス関数をキャンセル
      fn.cancel();
      // 即座に書き込み
      try {
        localStorage.setItem(key, JSON.stringify(pendingValue));
      } catch {
        // エラー時は無視
      }
    }
  }

  /**
   * 特定のキーの保留中の書き込みをフラッシュ
   */
  flushKey(key: string): void {
    const entry = this.debouncedSetters.get(key);
    if (entry) {
      entry.fn.cancel();
      try {
        localStorage.setItem(key, JSON.stringify(entry.pendingValue));
      } catch {
        // エラー時は無視
      }
    }
  }

  /**
   * キャッシュをクリア
   */
  clearCache(): void {
    this.cache.clear();
    for (const { fn } of this.debouncedSetters.values()) {
      fn.cancel();
    }
    this.debouncedSetters.clear();
  }

  /**
   * 特定のキーのキャッシュを無効化
   */
  disableCacheForKey(key: string): void {
    this.cache.delete(key);
  }

  /**
   * 特定のキーについてキャッシュを無効化して値を取得
   */
  getWithoutCache<T>(key: string, defaultValue: T): T {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return defaultValue;
      }
      return JSON.parse(stored);
    } catch {
      return defaultValue;
    }
  }
}
